/* components/ui/TelegramLinkFlow.jsx — привязка Telegram к аккаунту ЧЕРЕЗ БОТА.
 *
 * Кнопка → POST /api/auth/telegram/link-request/ → модалка с одноразовой ссылкой
 * t.me/<bot>?start=link_<token>. Пользователь открывает бота С ТОГО аккаунта Telegram,
 * который хочет привязать, и жмёт Start; поллер бота привязывает from.id, а модалка
 * опрашивает статус запроса раз в 2 с и закрывается по факту (onLinked).
 *
 * Почему не Login Widget / oauth.telegram.org: Telegram помнит вход в БРАУЗЕРЕ
 * (cookie на своём домене) и при уже авторизованном боте возвращал прежний аккаунт
 * без единого экрана — «отвязать → привязать другой» было невозможно, официального
 * выхода у Telegram нет (шрам S29). Deep-link делает выбор аккаунта явным, не зависит
 * от памяти браузера и не требует /setdomain в BotFather.
 */
import React from 'react';
import * as authApi from '../../lib/api/auth.js';
import { LINK_POLL_INTERVAL_MS, linkFailureText, linkOutcome } from '../../lib/telegramLink.js';
import { Button } from './Button.jsx';
import { Icon } from './Icon.jsx';
import { Modal } from './Modal.jsx';
import { Spinner } from './Spinner.jsx';

const { useCallback, useEffect, useRef, useState } = React;

export function TelegramLinkFlow({ onLinked, onError, label = 'Привязать Telegram' }) {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [req, setReq] = useState(null);       // {token, deep_link, bot_username, expires_at}
  const [phase, setPhase] = useState('idle'); // idle | creating | waiting | failed
  const [failure, setFailure] = useState('');  // статус провала (см. lib/telegramLink)
  const alive = useRef(true);
  // Колбэки родителя — через ref, чтобы их пересоздание на каждом рендере не
  // перезапускало таймер опроса.
  const onLinkedRef = useRef(onLinked);
  const onErrorRef = useRef(onError);
  useEffect(() => { onLinkedRef.current = onLinked; onErrorRef.current = onError; });

  useEffect(() => {
    alive.current = true;
    authApi.telegramConfig().then((r) => { if (alive.current && r.ok) setCfg(r.data); }).catch(() => {});
    return () => { alive.current = false; };
  }, []);

  const start = useCallback(async () => {
    setPhase('creating');
    let r;
    try { r = await authApi.telegramLinkRequest(); } catch (e) { r = { ok: false, status: 0, data: null }; }
    if (!alive.current) return;
    if (!r.ok) {
      setPhase('idle');
      setOpen(false);
      if (onErrorRef.current) onErrorRef.current(r);
      return;
    }
    setReq(r.data);
    setFailure('');
    setPhase('waiting');
    setOpen(true);
  }, []);

  // Опрос статуса, пока модалка открыта и ждём Start в боте.
  useEffect(() => {
    if (!open || phase !== 'waiting' || !req) return undefined;
    let stopped = false;
    const tick = async () => {
      let r;
      try { r = await authApi.telegramLinkRequestStatus(req.token); } catch (e) { return; }
      if (stopped || !alive.current) return;
      if (!r.ok) {
        if (r.status === 404) { setFailure('unknown'); setPhase('failed'); }
        return; // сеть моргнула / rate-limit — следующий тик
      }
      const outcome = linkOutcome(r.data && r.data.status);
      if (outcome === 'done') {
        setPhase('idle');
        setOpen(false);
        if (onLinkedRef.current) onLinkedRef.current(r.data);
      } else if (outcome === 'failed') {
        setFailure((r.data && r.data.status) || 'unknown');
        setPhase('failed');
      }
    };
    const id = setInterval(tick, LINK_POLL_INTERVAL_MS);
    return () => { stopped = true; clearInterval(id); };
  }, [open, phase, req]);

  const close = () => { setOpen(false); setPhase('idle'); };

  if (cfg && cfg.enabled === false) return null;
  const bot = (req && req.bot_username) || (cfg && cfg.bot_username) || '';
  return (
    <>
      <Button variant="secondary" iconLeft="send" onClick={start}
              loading={phase === 'creating'} disabled={!cfg}>
        {label}
      </Button>
      <Modal open={open} onClose={close} title="Привязка Telegram" size="sm" labelledBy="tg-link-title"
             footer={<Button variant="ghost" onClick={close}>Отмена</Button>}>
        <div className="stack stack-12">
          <ol className="stack stack-8 t-body-sm" style={{ margin: 0, paddingLeft: 20 }}>
            <li>Откройте бота <b>@{bot}</b> с того аккаунта Telegram, который хотите привязать.</li>
            <li>Нажмите <b>Start</b> (или отправьте команду /start).</li>
            <li>Вернитесь сюда — привязка подтвердится сама.</li>
          </ol>
          {req && phase === 'waiting' && (
            <div>
              <a className="btn btn--primary btn--md" href={req.deep_link}
                 target="_blank" rel="noopener noreferrer" data-testid="tg-deep-link">
                <Icon name="send" size={16} />
                <span className="btn__label">Открыть бота @{bot}</span>
              </a>
            </div>
          )}
          {phase === 'waiting' && (
            <Spinner size="sm" label="Ждём подтверждения в Telegram… Ссылка действует 15 минут." />
          )}
          {phase === 'failed' && (
            <div className="stack stack-8">
              <span className="t-body-sm" role="alert">{linkFailureText(failure)}</span>
              <div>
                <Button variant="secondary" size="sm" iconLeft="refresh" onClick={start}>Новая ссылка</Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
